import type { PageName } from '~/config'
import type { Controller } from '~/features/controller'
import type { ControllerState } from '~/features/controllerState'
import type { Identifiable } from '~/models/identifiable'

export abstract class Service<
  DataType extends Identifiable,
  ReturnType extends DataType[] | Record<string, DataType>,
> {
  protected subscribers: ControllerState<DataType, ReturnType>[]
  protected activePage!: PageName

  constructor() {
    this.subscribers = []
  }

  public subscribe(
    controller: Controller,
    sideEffect?: () => void | Promise<void>,
  ): ControllerState<DataType, ReturnType> {
    const controllerState = this.createControllerState(controller, sideEffect)

    const activeExistingState = this.getActiveExistingState()

    if (activeExistingState) {
      controllerState.setInitialState(activeExistingState)
    }

    this.subscribers.push(controllerState)

    return controllerState
  }

  private getActiveExistingState() {
    const activeSubscribers = this.subscribers.filter(
      subscriber => subscriber.isActive,
    )
    if (activeSubscribers.length > 0) {
      const firstControllerState = activeSubscribers[0]

      if (!firstControllerState.isLoading()) {
        return firstControllerState.state
      }
    }
  }

  public activatePage(pageName: PageName): void {
    this.activePage = pageName

    for (const subscriber of this.subscribers) {
      subscriber.activatePage(pageName)
    }
  }

  protected getEmptyState(): DataType {
    return {} as DataType
  }

  protected abstract createControllerState(
    controller: Controller,
    sideEffect?: () => void | Promise<void>
  ): ControllerState<DataType, ReturnType>

  protected updateSubscriberStates(data: ReturnType): void {
    for (const subscriber of this.subscribers) {
      subscriber.setState(data)
    }
  }
}
